import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import api from '../../../utils/api';
import './OrderConfigManager.css';

export default function OrderConfigManager() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Editable config state
  const [sectionTitles, setSectionTitles] = useState({
    serviceType: 'Service Type',
    cleaningCategory: 'Cleaning Category',
    alwaysIncluded: 'Always Included',
    asNeeded: 'As Needed',
    extraTargeted: 'Extra Targeted Cleaning',
    equipment: 'Equipment',
    bookingDetails: 'Booking Details',
  });
  const [sectionDescriptions, setSectionDescriptions] = useState({
    serviceType: 'Edit the labels for each service type. Core types (home, commercial, moveinout) cannot be removed. You can add custom ones.',
    cleaningCategory: 'Edit labels and descriptions. Core categories (standard, main, adhoc) cannot be removed. You can add custom ones.',
    alwaysIncluded: 'Tasks that are always performed during standard and main cleaning. You can edit, remove, or add new items.',
    asNeeded: 'Optional tasks the client can select during standard cleaning. Each has a key (stored in database) and a label (shown to users).',
    extraTargeted: 'Additional targeted cleaning options the client can request (e.g. animal hair, smoking).',
    equipment: 'Equipment the client can ask the cleaner to bring. Each has a key (stored in database) and a label (shown to users).',
    bookingDetails: '',
  });
  const [bookingDetailsLabels, setBookingDetailsLabels] = useState({
    service: 'Service',
    servicePlaceholder: 'e.g. Cleaning service',
    client: 'Client',
    chooseClient: 'Choose a client...',
    startDate: 'Start date',
    startTime: 'Start time',
    endTime: 'End time',
    to: 'To',
    allDay: 'All day',
    recurring: 'Recurring',
    repeatEvery: 'Repeat every',
    weekSuffix: 'week(s)',
    onDays: 'On days',
    untilDate: 'Until date',
    untilDateOptional: 'optional',
    untilDatePlaceholder: 'Leave blank to repeat for 1 year',
    address: 'Address',
    whatNeedsToBeDone: 'What needs to be done',
    checklistPlaceholder: 'e.g. Kitchen cleaning, Bathroom deep clean...',
    comments: 'Comments',
    saveEvent: 'Save',
    submitting: 'Submitting...',
    cancel: 'Cancel',
  });

  // Order + human-friendly hint for each editable Booking Details field.
  const BOOKING_DETAILS_FIELDS = [
    { key: 'service',              hint: 'Service label' },
    { key: 'servicePlaceholder',   hint: 'Service input placeholder' },
    { key: 'client',               hint: 'Client label (admin)' },
    { key: 'chooseClient',         hint: 'Client dropdown placeholder' },
    { key: 'startDate',            hint: 'Start date label' },
    { key: 'startTime',            hint: 'Start time label' },
    { key: 'endTime',              hint: 'End time label' },
    { key: 'to',                   hint: 'Separator between start & end time' },
    { key: 'allDay',               hint: 'All day checkbox label' },
    { key: 'recurring',            hint: 'Recurring checkbox label' },
    { key: 'repeatEvery',          hint: 'Repeat every label' },
    { key: 'weekSuffix',           hint: 'Suffix after "Repeat every N" (e.g. week(s))' },
    { key: 'onDays',               hint: 'On days label' },
    { key: 'untilDate',            hint: 'Until date label' },
    { key: 'untilDateOptional',    hint: '"(optional)" hint after Until date' },
    { key: 'untilDatePlaceholder', hint: 'Until date picker placeholder' },
    { key: 'address',              hint: 'Address field label' },
    { key: 'whatNeedsToBeDone',    hint: 'Checklist section label' },
    { key: 'checklistPlaceholder', hint: 'Checklist input placeholder' },
    { key: 'comments',             hint: 'Comments field label' },
    { key: 'saveEvent',            hint: 'Submit button text' },
    { key: 'submitting',           hint: 'Submit button text while saving' },
    { key: 'cancel',               hint: 'Cancel button text' },
  ];
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [cleaningCategoryOptions, setCleaningCategoryOptions] = useState([]);
  const [alwaysIncludedItems, setAlwaysIncludedItems] = useState([]);
  const [asNeededItems, setAsNeededItems] = useState([]);
  const [extraTargetedItems, setExtraTargetedItems] = useState([]);
  const [equipmentItems, setEquipmentItems] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  // Per-custom-section "new item" inputs, keyed by section id
  const [newCustomItem, setNewCustomItem] = useState({}); // { [sectionId]: { key: '', label: '' } }

  // "Add new" input state
  const [newAlwaysItem, setNewAlwaysItem] = useState('');
  const [newAsNeededKey, setNewAsNeededKey] = useState('');
  const [newAsNeededLabel, setNewAsNeededLabel] = useState('');
  const [newServiceValue, setNewServiceValue] = useState('');
  const [newServiceLabel, setNewServiceLabel] = useState('');
  const [newCategoryValue, setNewCategoryValue] = useState('');
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');
  const [newExtraTargetedKey, setNewExtraTargetedKey] = useState('');
  const [newExtraTargetedLabel, setNewExtraTargetedLabel] = useState('');
  const [newEquipmentKey, setNewEquipmentKey] = useState('');
  const [newEquipmentLabel, setNewEquipmentLabel] = useState('');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      // Request values auto-translated to the admin's current UI language,
      // so the page shows English labels in EN mode and Danish in DA mode.
      const res = await api.get(`/order-config?lang=${encodeURIComponent(language)}`);
      const data = res.data?.data;
      if (data) {
        if (data.sectionTitles) setSectionTitles(prev => ({ ...prev, ...data.sectionTitles }));
        if (data.sectionDescriptions) setSectionDescriptions(prev => ({ ...prev, ...data.sectionDescriptions }));
        if (Array.isArray(data.serviceTypeOptions)) setServiceTypeOptions(data.serviceTypeOptions);
        if (Array.isArray(data.cleaningCategoryOptions)) setCleaningCategoryOptions(data.cleaningCategoryOptions);
        if (Array.isArray(data.alwaysIncludedItems)) setAlwaysIncludedItems(data.alwaysIncludedItems);
        if (Array.isArray(data.asNeededItems)) setAsNeededItems(data.asNeededItems);
        if (Array.isArray(data.extraTargetedItems)) setExtraTargetedItems(data.extraTargetedItems);
        if (Array.isArray(data.equipmentItems)) setEquipmentItems(data.equipmentItems);
        if (Array.isArray(data.customSections)) setCustomSections(data.customSections);
        if (data.bookingDetailsLabels) setBookingDetailsLabels(prev => ({ ...prev, ...data.bookingDetailsLabels }));
      }
    } catch {
      setMessage({ text: t('admin.configLoadError', 'Failed to load configuration'), type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t, language]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const res = await api.put('/order-config', {
        sectionTitles,
        sectionDescriptions,
        serviceTypeOptions,
        cleaningCategoryOptions,
        alwaysIncludedItems,
        asNeededItems,
        extraTargetedItems,
        equipmentItems,
        customSections,
        bookingDetailsLabels,
      });
      const data = res.data?.data;
      if (data) {
        if (data.sectionTitles) setSectionTitles(prev => ({ ...prev, ...data.sectionTitles }));
        if (data.sectionDescriptions) setSectionDescriptions(prev => ({ ...prev, ...data.sectionDescriptions }));
        if (Array.isArray(data.serviceTypeOptions)) setServiceTypeOptions(data.serviceTypeOptions);
        if (Array.isArray(data.cleaningCategoryOptions)) setCleaningCategoryOptions(data.cleaningCategoryOptions);
        if (Array.isArray(data.alwaysIncludedItems)) setAlwaysIncludedItems(data.alwaysIncludedItems);
        if (Array.isArray(data.asNeededItems)) setAsNeededItems(data.asNeededItems);
        if (Array.isArray(data.extraTargetedItems)) setExtraTargetedItems(data.extraTargetedItems);
        if (Array.isArray(data.equipmentItems)) setEquipmentItems(data.equipmentItems);
        if (Array.isArray(data.customSections)) setCustomSections(data.customSections);
        if (data.bookingDetailsLabels) setBookingDetailsLabels(prev => ({ ...prev, ...data.bookingDetailsLabels }));
      }
      setMessage({ text: t('admin.configSaved', 'Configuration saved successfully!'), type: 'success' });
    } catch (err) {
      setMessage({ text: err.response?.data?.message || t('admin.configSaveError', 'Failed to save configuration'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // --- Service Type helpers ---
  const REQUIRED_SERVICE_VALUES = ['home', 'commercial', 'moveinout'];
  const updateServiceTypeLabel = (value, label) => {
    setServiceTypeOptions(prev => prev.map(o => o.value === value ? { ...o, label } : o));
  };
  const removeServiceType = (value) => {
    setServiceTypeOptions(prev => prev.filter(o => o.value !== value));
  };
  const addServiceType = () => {
    const value = newServiceValue.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const label = newServiceLabel.trim();
    if (!value || !label) return;
    if (serviceTypeOptions.some(o => o.value === value)) return;
    setServiceTypeOptions(prev => [...prev, { value, label }]);
    setNewServiceValue('');
    setNewServiceLabel('');
  };

  // --- Cleaning Category helpers ---
  const REQUIRED_CATEGORY_VALUES = ['standard', 'main', 'adhoc'];
  const updateCategoryField = (value, field, val) => {
    setCleaningCategoryOptions(prev => prev.map(o => o.value === value ? { ...o, [field]: val } : o));
  };
  const removeCategory = (value) => {
    setCleaningCategoryOptions(prev => prev.filter(o => o.value !== value));
  };
  const addCategory = () => {
    const value = newCategoryValue.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    const label = newCategoryLabel.trim();
    if (!value || !label) return;
    if (cleaningCategoryOptions.some(o => o.value === value)) return;
    setCleaningCategoryOptions(prev => [...prev, { value, label, description: newCategoryDesc.trim() }]);
    setNewCategoryValue('');
    setNewCategoryLabel('');
    setNewCategoryDesc('');
  };

  // --- Always Included helpers ---
  const updateAlwaysItem = (index, val) => {
    setAlwaysIncludedItems(prev => prev.map((item, i) => i === index ? val : item));
  };
  const removeAlwaysItem = (index) => {
    setAlwaysIncludedItems(prev => prev.filter((_, i) => i !== index));
  };
  const addAlwaysItem = () => {
    const trimmed = newAlwaysItem.trim();
    if (!trimmed) return;
    setAlwaysIncludedItems(prev => [...prev, trimmed]);
    setNewAlwaysItem('');
  };

  // --- As Needed helpers ---
  const updateAsNeededField = (index, field, val) => {
    setAsNeededItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: val } : item));
  };
  const removeAsNeededItem = (index) => {
    setAsNeededItems(prev => prev.filter((_, i) => i !== index));
  };
  const addAsNeededItem = () => {
    const key = newAsNeededKey.trim();
    const label = newAsNeededLabel.trim();
    if (!key || !label) return;
    setAsNeededItems(prev => [...prev, { key, label }]);
    setNewAsNeededKey('');
    setNewAsNeededLabel('');
  };

  // --- Extra Targeted helpers ---
  const updateExtraTargetedField = (index, field, val) => {
    setExtraTargetedItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: val } : item));
  };
  const removeExtraTargetedItem = (index) => {
    setExtraTargetedItems(prev => prev.filter((_, i) => i !== index));
  };
  const addExtraTargetedItem = () => {
    const key = newExtraTargetedKey.trim();
    const label = newExtraTargetedLabel.trim();
    if (!key || !label) return;
    if (extraTargetedItems.some(i => i.key === key)) return;
    setExtraTargetedItems(prev => [...prev, { key, label }]);
    setNewExtraTargetedKey('');
    setNewExtraTargetedLabel('');
  };

  // --- Equipment helpers ---
  const updateEquipmentField = (index, field, val) => {
    setEquipmentItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: val } : item));
  };
  const removeEquipmentItem = (index) => {
    setEquipmentItems(prev => prev.filter((_, i) => i !== index));
  };
  const addEquipmentItem = () => {
    const key = newEquipmentKey.trim();
    const label = newEquipmentLabel.trim();
    if (!key || !label) return;
    if (equipmentItems.some(i => i.key === key)) return;
    setEquipmentItems(prev => [...prev, { key, label }]);
    setNewEquipmentKey('');
    setNewEquipmentLabel('');
  };

  // --- Custom Sections helpers ---
  const addCustomSection = () => {
    const newSec = {
      id: `cs_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: '',
      description: '',
      items: [],
    };
    setCustomSections(prev => [...prev, newSec]);
  };
  const removeCustomSection = (id) => {
    if (!window.confirm(t('admin.confirmDeleteSection', 'Delete this section and all its items?'))) return;
    setCustomSections(prev => prev.filter(s => s.id !== id));
    setNewCustomItem(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const updateCustomSectionField = (id, field, value) => {
    setCustomSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };
  const updateCustomSectionItem = (sectionId, index, field, value) => {
    setCustomSections(prev => prev.map(s =>
      s.id === sectionId
        ? { ...s, items: s.items.map((it, i) => i === index ? { ...it, [field]: value } : it) }
        : s
    ));
  };
  const removeCustomSectionItem = (sectionId, index) => {
    setCustomSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, items: s.items.filter((_, i) => i !== index) } : s
    ));
  };
  const updateNewCustomItemField = (sectionId, field, value) => {
    setNewCustomItem(prev => ({
      ...prev,
      [sectionId]: { ...(prev[sectionId] || { key: '', label: '' }), [field]: value },
    }));
  };
  const addCustomSectionItem = (sectionId) => {
    const draft = newCustomItem[sectionId] || { key: '', label: '' };
    const key = (draft.key || '').trim();
    const label = (draft.label || '').trim();
    if (!key || !label) return;
    const section = customSections.find(s => s.id === sectionId);
    if (section && section.items.some(i => i.key === key)) return;
    setCustomSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, items: [...s.items, { key, label }] } : s
    ));
    setNewCustomItem(prev => ({ ...prev, [sectionId]: { key: '', label: '' } }));
  };

  if (loading) {
    return <div className="ocm-loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="ocm-container">
      <div className="ocm-header">
        <h2>{t('admin.orderConfig', 'Order Form Configuration')}</h2>
        <button className="btn btn-primary ocm-save-btn" onClick={saveConfig} disabled={saving}>
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save Changes')}
        </button>
      </div>

      {message.text && (
        <div className={`ocm-message ocm-message-${message.type}`}>{message.text}</div>
      )}

      {/* ===== Service Type Labels ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.serviceType}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, serviceType: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.serviceType}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, serviceType: e.target.value }))}
        />
        <div className="ocm-items">
          {serviceTypeOptions.map(opt => (
            <div key={opt.value} className="ocm-row">
              <span className="ocm-row-key">{opt.value}</span>
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={opt.label}
                onChange={(e) => updateServiceTypeLabel(opt.value, e.target.value)}
                placeholder="Label"
              />
              {!REQUIRED_SERVICE_VALUES.includes(opt.value) && (
                <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeServiceType(opt.value)}>
                  {t('common.delete', 'Delete')}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-key"
            value={newServiceValue}
            onChange={(e) => setNewServiceValue(e.target.value)}
            placeholder={t('admin.valuePlaceholder', 'Value (e.g. office)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newServiceLabel}
            onChange={(e) => setNewServiceLabel(e.target.value)}
            placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addServiceType(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addServiceType}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== Cleaning Category Labels & Descriptions ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.cleaningCategory}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, cleaningCategory: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.cleaningCategory}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, cleaningCategory: e.target.value }))}
        />
        <div className="ocm-items">
          {cleaningCategoryOptions.map(opt => (
            <div key={opt.value} className="ocm-card">
              <span className="ocm-row-key">{opt.value}</span>
              <div className="ocm-card-fields">
                <input
                  type="text"
                  className="form-control ocm-input"
                  value={opt.label}
                  onChange={(e) => updateCategoryField(opt.value, 'label', e.target.value)}
                  placeholder="Label"
                />
                <input
                  type="text"
                  className="form-control ocm-input"
                  value={opt.description || ''}
                  onChange={(e) => updateCategoryField(opt.value, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
              {!REQUIRED_CATEGORY_VALUES.includes(opt.value) && (
                <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeCategory(opt.value)}>
                  {t('common.delete', 'Delete')}
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-key"
            value={newCategoryValue}
            onChange={(e) => setNewCategoryValue(e.target.value)}
            placeholder={t('admin.valuePlaceholder', 'Value (e.g. office)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newCategoryLabel}
            onChange={(e) => setNewCategoryLabel(e.target.value)}
            placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newCategoryDesc}
            onChange={(e) => setNewCategoryDesc(e.target.value)}
            placeholder={t('admin.descPlaceholder', 'Description (optional)')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addCategory}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== Always Included ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.alwaysIncluded}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, alwaysIncluded: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.alwaysIncluded}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, alwaysIncluded: e.target.value }))}
        />
        <div className="ocm-items">
          {alwaysIncludedItems.map((item, i) => (
            <div key={i} className="ocm-row">
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={item}
                onChange={(e) => updateAlwaysItem(i, e.target.value)}
              />
              <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeAlwaysItem(i)}>
                {t('common.delete', 'Delete')}
              </button>
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newAlwaysItem}
            onChange={(e) => setNewAlwaysItem(e.target.value)}
            placeholder={t('admin.addAlwaysItemPlaceholder', 'New always-included item...')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlwaysItem(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addAlwaysItem}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== As Needed ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.asNeeded}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, asNeeded: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.asNeeded}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, asNeeded: e.target.value }))}
        />
        <div className="ocm-items">
          {asNeededItems.map((item, i) => (
            <div key={i} className="ocm-row">
              <input
                type="text"
                className="form-control ocm-input ocm-input-key"
                value={item.key}
                onChange={(e) => updateAsNeededField(i, 'key', e.target.value)}
                placeholder="Key"
              />
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={item.label}
                onChange={(e) => updateAsNeededField(i, 'label', e.target.value)}
                placeholder="Label"
              />
              <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeAsNeededItem(i)}>
                {t('common.delete', 'Delete')}
              </button>
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-key"
            value={newAsNeededKey}
            onChange={(e) => setNewAsNeededKey(e.target.value)}
            placeholder={t('admin.keyPlaceholder', 'Key (e.g. newTask)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newAsNeededLabel}
            onChange={(e) => setNewAsNeededLabel(e.target.value)}
            placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAsNeededItem(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addAsNeededItem}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== Extra Targeted Cleaning ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.extraTargeted}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, extraTargeted: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.extraTargeted}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, extraTargeted: e.target.value }))}
        />
        <div className="ocm-items">
          {extraTargetedItems.map((item, i) => (
            <div key={i} className="ocm-row">
              <input
                type="text"
                className="form-control ocm-input ocm-input-key"
                value={item.key}
                onChange={(e) => updateExtraTargetedField(i, 'key', e.target.value)}
                placeholder="Key"
              />
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={item.label}
                onChange={(e) => updateExtraTargetedField(i, 'label', e.target.value)}
                placeholder="Label"
              />
              <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeExtraTargetedItem(i)}>
                {t('common.delete', 'Delete')}
              </button>
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-key"
            value={newExtraTargetedKey}
            onChange={(e) => setNewExtraTargetedKey(e.target.value)}
            placeholder={t('admin.keyPlaceholder', 'Key (e.g. animalHair)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newExtraTargetedLabel}
            onChange={(e) => setNewExtraTargetedLabel(e.target.value)}
            placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtraTargetedItem(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addExtraTargetedItem}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== Equipment ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.equipment}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, equipment: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.equipment}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, equipment: e.target.value }))}
        />
        <div className="ocm-items">
          {equipmentItems.map((item, i) => (
            <div key={i} className="ocm-row">
              <input
                type="text"
                className="form-control ocm-input ocm-input-key"
                value={item.key}
                onChange={(e) => updateEquipmentField(i, 'key', e.target.value)}
                placeholder="Key"
              />
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={item.label}
                onChange={(e) => updateEquipmentField(i, 'label', e.target.value)}
                placeholder="Label"
              />
              <button type="button" className="btn btn-danger ocm-delete-btn" onClick={() => removeEquipmentItem(i)}>
                {t('common.delete', 'Delete')}
              </button>
            </div>
          ))}
        </div>
        <div className="ocm-add-row">
          <input
            type="text"
            className="form-control ocm-input ocm-input-key"
            value={newEquipmentKey}
            onChange={(e) => setNewEquipmentKey(e.target.value)}
            placeholder={t('admin.keyPlaceholder', 'Key (e.g. mop)')}
          />
          <input
            type="text"
            className="form-control ocm-input ocm-input-wide"
            value={newEquipmentLabel}
            onChange={(e) => setNewEquipmentLabel(e.target.value)}
            placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEquipmentItem(); } }}
          />
          <button type="button" className="btn btn-secondary ocm-add-btn" onClick={addEquipmentItem}>
            {t('common.add', 'Add')}
          </button>
        </div>
      </div>

      {/* ===== Booking Details ===== */}
      <div className="ocm-section">
        <h3 className="ocm-section-title">
          <input
            type="text"
            className="ocm-title-input"
            value={sectionTitles.bookingDetails}
            onChange={(e) => setSectionTitles(prev => ({ ...prev, bookingDetails: e.target.value }))}
          />
        </h3>
        <textarea
          className="ocm-desc-input"
          rows={2}
          value={sectionDescriptions.bookingDetails}
          onChange={(e) => setSectionDescriptions(prev => ({ ...prev, bookingDetails: e.target.value }))}
          placeholder={t('admin.sectionSubtitlePlaceholder', 'Subtitle shown under the title (optional)')}
        />
        <div className="ocm-items">
          {BOOKING_DETAILS_FIELDS.map(({ key, hint }) => (
            <div key={key} className="ocm-row">
              <span className="ocm-row-key">{key}</span>
              <input
                type="text"
                className="form-control ocm-input ocm-input-wide"
                value={bookingDetailsLabels[key] || ''}
                onChange={(e) => setBookingDetailsLabels(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={hint}
              />
            </div>
          ))}
        </div>
      </div>

      {/* ===== Custom Sections ===== */}
      <div className="ocm-section ocm-custom-sections">
        <div className="ocm-custom-header">
          <h3 className="ocm-section-title" style={{ margin: 0 }}>
            {t('admin.customSections', 'Custom Sections')}
          </h3>
          <button type="button" className="btn btn-primary ocm-add-btn" onClick={addCustomSection}>
            + {t('admin.addNewSection', 'Add New Section')}
          </button>
        </div>
        <p className="ocm-custom-hint">
          {t('admin.customSectionsHint', 'Create your own categories with a title, subtitle, and options. They show up on the New Event page as checkbox groups.')}
        </p>

        {customSections.length === 0 && (
          <div className="ocm-custom-empty">
            {t('admin.noCustomSections', 'No custom sections yet. Click "Add New Section" to create one.')}
          </div>
        )}

        {customSections.map((sec) => {
          const draft = newCustomItem[sec.id] || { key: '', label: '' };
          return (
            <div key={sec.id} className="ocm-custom-card">
              <div className="ocm-custom-card-header">
                <input
                  type="text"
                  className="form-control ocm-input ocm-input-wide ocm-custom-title-input"
                  value={sec.title}
                  onChange={(e) => updateCustomSectionField(sec.id, 'title', e.target.value)}
                  placeholder={t('admin.customSectionTitlePlaceholder', 'Section title (e.g. Pet Care)')}
                />
                <button
                  type="button"
                  className="btn btn-danger ocm-delete-btn"
                  onClick={() => removeCustomSection(sec.id)}
                  title={t('admin.deleteSection', 'Delete this section')}
                >
                  {t('common.delete', 'Delete')}
                </button>
              </div>
              <textarea
                className="ocm-desc-input"
                rows={2}
                value={sec.description}
                onChange={(e) => updateCustomSectionField(sec.id, 'description', e.target.value)}
                placeholder={t('admin.customSectionDescPlaceholder', 'Subtitle / description shown under the title')}
              />

              <div className="ocm-items">
                {sec.items.map((item, i) => (
                  <div key={i} className="ocm-row">
                    <input
                      type="text"
                      className="form-control ocm-input ocm-input-key"
                      value={item.key}
                      onChange={(e) => updateCustomSectionItem(sec.id, i, 'key', e.target.value)}
                      placeholder="Key"
                    />
                    <input
                      type="text"
                      className="form-control ocm-input ocm-input-wide"
                      value={item.label}
                      onChange={(e) => updateCustomSectionItem(sec.id, i, 'label', e.target.value)}
                      placeholder="Label"
                    />
                    <button
                      type="button"
                      className="btn btn-danger ocm-delete-btn"
                      onClick={() => removeCustomSectionItem(sec.id, i)}
                    >
                      {t('common.delete', 'Delete')}
                    </button>
                  </div>
                ))}
              </div>

              <div className="ocm-add-row">
                <input
                  type="text"
                  className="form-control ocm-input ocm-input-key"
                  value={draft.key}
                  onChange={(e) => updateNewCustomItemField(sec.id, 'key', e.target.value)}
                  placeholder={t('admin.keyPlaceholder', 'Key (e.g. option1)')}
                />
                <input
                  type="text"
                  className="form-control ocm-input ocm-input-wide"
                  value={draft.label}
                  onChange={(e) => updateNewCustomItemField(sec.id, 'label', e.target.value)}
                  placeholder={t('admin.labelPlaceholder', 'Label (shown to users)')}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSectionItem(sec.id); } }}
                />
                <button
                  type="button"
                  className="btn btn-secondary ocm-add-btn"
                  onClick={() => addCustomSectionItem(sec.id)}
                >
                  {t('common.add', 'Add')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
